import { ProductCartItem } from '@design-system/product-cart-item';
import { getShopifyImage } from 'gatsby-source-shopify';
import React, { FC, useCallback, useContext, useMemo, useState } from 'react';

import { StoreContext } from '~/context/shopify';
import { formatPrice } from '~/utils/currencyFormatting';

import { CartItemWrapperProps } from './models';

const CartItemWrapper: FC<CartItemWrapperProps> = ({
  id,
  title,
  productVariant,
  quantity,
  productCartItemSettings: {
    giftLabel,
    priceLabel,
    removeButton,
    quantityButton,
  },
  customAttributes,
  handleRemoveItem,
}) => {
  const { checkout, updateLineItem } = useContext(StoreContext);
  const [quantityNumber, setQuantityNumber] = useState(quantity);
  const [maxQuantity] = useState(productVariant.inventoryQuantity);

  const updateItem = useCallback(
    (value) => updateLineItem(checkout.id, id, value),
    []
  );

  const handleQuantityChange = (value) => {
    const newValue = Number(value);
    setQuantityNumber(newValue);

    if (newValue >= 1 && maxQuantity && newValue <= maxQuantity)
      updateItem(newValue);
  };

  const quantityBlock = {
    id: id.toString(),
    value: quantityNumber,
    onChange: handleQuantityChange,
    ...quantityButton,
    max: maxQuantity,
  };

  const variantImage = {
    ...productVariant.image,
    originalSrc: productVariant.image.src,
  };

  const image = useMemo(
    () =>
      getShopifyImage({
        image: variantImage,
        layout: 'constrained',
        fit: 'contain',
        width: 200,
        height: 200,
      }),
    [variantImage.src]
  );

  const headingBlock = {
    headline: title,
    description: customAttributes.find((attr) => attr.key === 'description')
      ?.value,
  };

  const discountedPriceAmount = productVariant.compareAtPriceV2?.amount;

  const priceBlock = {
    discountedPrice: discountedPriceAmount
      ? formatPrice(
          productVariant.priceV2.currencyCode,
          Number(discountedPriceAmount) * quantity
        )
      : undefined,
    price: formatPrice(
      productVariant.priceV2.currencyCode,
      Number(productVariant.priceV2.amount) * quantity
    ),
  };

  return (
    <ProductCartItem
      className="shopping-cart__product"
      quantityBlock={quantityBlock}
      id={id}
      image={{ props: { ...image.images.fallback! } }}
      headingBlock={headingBlock}
      priceBlock={{
        ...priceBlock,
        giftLabel,
        priceLabel,
        removeButton,
      }}
      productLink={{
        props: {
          href: customAttributes.find((attr) => attr.key === 'link')
            ?.value as string,
        },
      }}
      handleRemoveItem={() => handleRemoveItem(id)}
    />
  );
};

export default CartItemWrapper;
